/**
 * Shared constants and helpers for Google Maps + deck.gl/WebGL map surfaces.
 *
 * ## DEFAULT_MAP_CENTER / DEFAULT_MAP_ZOOM
 *
 * Geographic center of the contiguous US (near Lebanon, KS) at a continent-wide
 * zoom. Use these as `defaultCenter` / `defaultZoom` on a `<Map>` so the
 * uncontrolled first paint shows a sensible nationwide view while customer or
 * route data is still loading.
 *
 * Do NOT hardcode city-level coordinates as a default (e.g. Orlando). A
 * city-default biases non-local haulers into a wrong-region first paint, and
 * any controlled `center` initialized to that value will race against a later
 * `fitBounds` animation — the controlled prop can re-pin the map mid-animation
 * and leave the user looking at the wrong place. Customer-region centering
 * should come from `fitBounds` (which extends bounds with the customer
 * lat/lng), not from a hardcoded default.
 *
 * ## isValidLatLng
 *
 * Filter for coordinate pairs before extending a `LatLngBounds`. The pair
 * `(0, 0)` is finite but lives in the Atlantic off Africa — including it in
 * bounds drags `fitBounds` out to a world view, which is the symptom haulers
 * see when a stop arrives without resolved coordinates after an assign
 * mutation. Treat `(0, 0)` as missing data.
 *
 * Per-axis zeros (e.g. `(0, -74)`) are *not* filtered: those are real points
 * on the equator or prime meridian and may be legitimate.
 */

export const DEFAULT_MAP_CENTER = Object.freeze({ lat: 39.82835, lng: -98.5795 })

export const DEFAULT_MAP_ZOOM = 4

// Shared max zoom for every interactive Google `<Map>` so they match the legacy
// Mapbox route map's deeper zoom. Google clamps to the imagery it actually has
// (vector basemaps reach ~22), so a high ceiling just removes a premature cap.
export const GOOGLE_MAPS_MAX_ZOOM = 22

export const isValidLatLng = (lat, lng) => Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)

// Coordinate sinks across the Google Maps API (panTo/fitBounds/setCenter,
// Polyline paths, LatLngBounds.extend) throw a synchronous InvalidValueError on
// a NaN/Infinity coordinate, which white-screens the surface (CS-2889). Filter
// data-derived coordinates through these before handing them to the maps API.

// Keep the valid { lat, lng } points, coerced to numbers — the shape panTo and
// Polyline paths use. Returning numbers (not the raw strings) keeps the maps API
// happy and downstream consistent.
export const filterValidLatLngs = points =>
  (Array.isArray(points) ? points : []).flatMap(p => {
    const lat = parseFloat(p?.lat)
    const lng = parseFloat(p?.lng)
    return isValidLatLng(lat, lng) ? [{ ...p, lat, lng }] : []
  })

// Keep the valid [lng, lat] GeoJSON-style pairs, coerced to numbers — the shape
// fitBounds / getGoogleMapsBounds inputs use.
export const filterValidCoordPairs = pairs =>
  (Array.isArray(pairs) ? pairs : []).flatMap(c => {
    if (!Array.isArray(c) || c.length !== 2) return []
    const lng = parseFloat(c[0])
    const lat = parseFloat(c[1])
    return isValidLatLng(lat, lng) ? [[lng, lat]] : []
  })
